import * as chrono from "chrono-node";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { XIcon } from "lucide-react";

import { Popper } from "@/components/atoms/Popper";
import { icons } from "@/objects/@icons";
import { List } from "@/components/organisms/List";
import {
  CellProps,
  useContextCellNavigation,
  useContextHotkeys,
  useContextScope,
} from "@/hooks/useContextTree";

import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

const getPreviousMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  if (month === 0) {
    return new Date(year - 1, 11, 1);
  }
  return new Date(year, month - 1, 1);
};

const getNextMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  if (month === 11) {
    return new Date(year + 1, 0, 1);
  }
  return new Date(year, month + 1, 1);
};

const datesEqual = (internalDate1: Date, internalDate2: Date) => {
  if (!internalDate1 || !internalDate2) {
    return false;
  }

  return (
    format(internalDate1, "yyyy-MM-dd") === format(internalDate2, "yyyy-MM-dd")
  );
};

function DateInput({
  date,
  otherDate,
  setDate,
  setOtherDate,
  isDateActive,
  activeDate,
  setActiveDate,
  setActiveYear,
  setActiveMonth,
  emptyText,
}: {
  date: Date;
  otherDate: Date;
  setDate: (date: Date) => void;
  setOtherDate: (date: Date) => void;
  isDateActive: boolean;
  activeDate: "internalDate1" | "internalDate2";
  setActiveDate: (date: "internalDate1" | "internalDate2") => void;
  setActiveYear: (year: number) => void;
  setActiveMonth: (month: number) => void;
  emptyText: string;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [focusedValue, setFocusedValue] = useState("");
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(
      isDateActive
        ? `${Utils.fLocalMonthDay(date)}, ${date.getFullYear()}`
        : "",
    );
  }, [date, isDateActive, emptyText]);

  return (
    <InputGroup
      onClick={() => {
        setActiveDate(activeDate);
      }}
    >
      <InputGroupInput
        value={isFocused ? focusedValue : value}
        placeholder={emptyText + "..."}
        onFocus={() => {
          setIsFocused(true);
          setFocusedValue(value);
        }}
        // TODO: the blur method exists, but typescript doesn't know about it.
        onKeyDown={(e: any) => {
          if (e.key === "Enter") {
            e.target.blur();
          }
        }}
        onChange={(e) => {
          setFocusedValue(e.target.value);
        }}
        onBlur={() => {
          const parsedDate = chrono.parseDate(focusedValue);
          if (parsedDate) {
            setActiveYear(parsedDate.getFullYear());
            setActiveMonth(parsedDate.getMonth() + 1);

            if (activeDate === "internalDate1" && parsedDate > otherDate) {
              setOtherDate(null);
            } else if (
              activeDate === "internalDate2" &&
              parsedDate < otherDate
            ) {
              setOtherDate(null);
            }
            setDate(parsedDate);
          }
          setFocusedValue("");
          setIsFocused(false);
        }}
        type="text"
        className="text-foreground text-ellipsis overflow-hidden whitespace-nowrap flex"
      />
      <InputGroupAddon align="inline-end">
        {
          <div className="pr-0.5">
            <Button
              variant="secondary"
              size="icon-xs"
              disabled={!isDateActive}
              onClick={() => {
                setValue("");
                setFocusedValue("");
                setDate(null);
              }}
              className=""
            >
              <XIcon />
            </Button>
          </div>
        }
      </InputGroupAddon>
    </InputGroup>
  );
}

function CalendarMonth({
  datesInActiveMonth,
  activeMonth,
  activeDate,
  setActiveDate,
  hoveringDate,
  setHoveringDate,
  internalDate1,
  setInternalDate1,
  internalDate2,
  setInternalDate2,
  includeDate2 = false,
  getCellProps,
  rowOffset = 0,
  colOffset = 0,
}: {
  datesInActiveMonth: Date[];
  activeMonth: number;
  activeDate: "internalDate1" | "internalDate2";
  hoveringDate: Date;
  setHoveringDate: (date: Date) => void;
  setActiveDate: (date: "internalDate1" | "internalDate2") => void;
  internalDate1: Date;
  setInternalDate1: (date: Date) => void;
  internalDate2: Date;
  setInternalDate2: (date: Date) => void;
  includeDate2?: boolean;
  getCellProps?: (opts: { rowIndex: number; colIndex: number }) => CellProps;
  rowOffset?: number;
  colOffset?: number;
}) {
  const setDate =
    activeDate === "internalDate2" ? setInternalDate2 : setInternalDate1;

  const otherDate =
    activeDate === "internalDate2" ? internalDate1 : internalDate2;

  const inRangeBackgroundColor = "bg-accent";

  return (
    <>
      <div
        onMouseLeave={() => setHoveringDate(null)}
        className="min-w-0 flex-wrap grid grid-cols-[repeat(7,32px)] auto-rows-[32px] gap-1"
      >
        {datesInActiveMonth.map((date, index) => {
          const rowIndex = Math.floor(index / 7) + rowOffset;
          const colIndex = (index % 7) + colOffset;
          const cellProps = getCellProps?.({ rowIndex, colIndex });

          const color =
            date.getMonth() === activeMonth - 1
              ? "text.primary"
              : "text.tertiary";
          let border = "border-none";
          let borderColor = "border-transparent";
          let backgroundColor = "bg-transparent";

          const isOtherDate = datesEqual(date, otherDate);

          // Active date is internal date 1
          if (!includeDate2) {
            if (date == hoveringDate) {
              border = "border-dashed";
              // backgroundColor = inRangeBackgroundColor;
            } else if (date == internalDate1) {
              border = "border-solid";
              borderColor = "border-[var(--primary)]";
            }
          } else if (activeDate === "internalDate1") {
            if (!hoveringDate) {
              if (!internalDate1 && internalDate2) {
                if (date <= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else if (internalDate1 && !internalDate2) {
                if (date >= internalDate1) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else if (internalDate1 && internalDate2) {
                if (date >= internalDate1 && date <= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else {
                backgroundColor = inRangeBackgroundColor;
              }
            } else {
              if (!internalDate1 && internalDate2) {
                if (date >= hoveringDate && date <= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate > internalDate2) {
                  border = "border-dashed";
                  borderColor = "border-foreground";
                }
              } else if (internalDate1 && !internalDate2) {
                if (date >= internalDate1 && date <= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate > internalDate2) {
                  border = "border-dashed";
                  borderColor = "border-foreground";
                }
              } else if (internalDate1 && internalDate2) {
                if (date >= hoveringDate && date <= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate > internalDate2) {
                  border = "border-dashed";
                  borderColor = "border-foreground";
                }
              } else {
                if (date >= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
              }
            }
          }
          // Active date is internal date 2
          else if (activeDate === "internalDate2") {
            if (!hoveringDate) {
              if (!internalDate1 && internalDate2) {
                if (date >= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else if (internalDate1 && !internalDate2) {
                if (date >= internalDate1) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else if (internalDate1 && internalDate2) {
                if (date >= internalDate1 && date <= internalDate2) {
                  backgroundColor = inRangeBackgroundColor;
                }
              } else {
                backgroundColor = inRangeBackgroundColor;
              }
            } else {
              if (!internalDate1 && internalDate2) {
                if (date >= internalDate2 && date <= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate < internalDate1) {
                  border = "border-dashed";
                  borderColor = "border-border";
                }
              } else if (internalDate1 && !internalDate2) {
                if (date >= internalDate1 && date <= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate < internalDate1) {
                  border = "border-dashed";
                  borderColor = "border-foreground";
                }
              } else if (internalDate1 && internalDate2) {
                if (date >= internalDate1 && date <= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
                if (isOtherDate && hoveringDate < internalDate1) {
                  border = "border-dashed";
                  borderColor = "border-foreground";
                }
              } else {
                if (date <= hoveringDate) {
                  backgroundColor = inRangeBackgroundColor;
                }
              }
            }
          }

          if (
            activeDate === "internalDate1" &&
            datesEqual(date, internalDate1)
          ) {
            if (includeDate2) {
              border = "border-dashed";
              borderColor = "border-muted-foreground/80";
            } else {
              border = "border-dashed";
              borderColor = "border-muted-foreground/80";
              backgroundColor = inRangeBackgroundColor;
            }
          } else if (
            activeDate === "internalDate2" &&
            datesEqual(date, internalDate2)
          ) {
            border = "border-dashed";
            borderColor = "border-muted-foreground/80";
          }

          return (
            <div
              {...cellProps}
              tabIndex={0}
              onMouseEnter={() => setHoveringDate(date)}
              onClick={() => {
                setHoveringDate(null);
                if (includeDate2) {
                  if (
                    activeDate === "internalDate1" &&
                    internalDate2 &&
                    date > internalDate2
                  ) {
                    setInternalDate2(null);
                    // toggleActiveDate = false;
                  } else if (
                    activeDate === "internalDate2" &&
                    internalDate1 &&
                    date < internalDate1
                  ) {
                    setInternalDate1(null);
                    // toggleActiveDate = false;
                  }
                  setActiveDate(
                    activeDate === "internalDate1"
                      ? "internalDate2"
                      : "internalDate1",
                  );
                }
                setDate(date);
              }}
              key={format(date, "yyyy-MM-dd")}
              className={cn(
                "min-w-0 flex items-center justify-center cursor-pointer rounded-sm border-[1px] [outline:none]",
                border,
                borderColor,
                backgroundColor,
                "hover:border-dashed hover:border-muted-foreground hover:bg-accent",
                "focus:border-dashed focus:border-muted-foreground focus:bg-accent",
                "focus-visible:[box-shadow:0_0_0_2px_var(--ring)]",
              )}
            >
              <Typography
                level="body-md"
                className={cn(
                  color === "text.primary"
                    ? "text-foreground"
                    : color === "text.tertiary"
                      ? "text-muted-foreground/80"
                      : "",
                )}
              >
                {format(date, "d")}
              </Typography>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Helper to compute dates for a given month
const computeDatesForMonth = (year: number, month: number) => {
  const startOfActiveMonth = subMonths(startOfMonth(new Date(year, month)), 1);
  const endOfActiveMonth = endOfMonth(startOfActiveMonth);
  let datesInActiveMonth = eachDayOfInterval({
    start: startOfActiveMonth,
    end: endOfActiveMonth,
  });

  const firstDayOfActiveMonth = datesInActiveMonth[0];
  const firstDayIndex = firstDayOfActiveMonth.getDay();

  const firstDayOfPreviousActiveMonth = subMonths(firstDayOfActiveMonth, 1);
  const lastDateOfPreviousActiveMonth = subDays(firstDayOfActiveMonth, 1);

  const datesOfPreviousMonth = eachDayOfInterval({
    start: firstDayOfPreviousActiveMonth,
    end: lastDateOfPreviousActiveMonth,
  });

  const firstDayOfNextActiveMonth = startOfMonth(
    addMonths(firstDayOfActiveMonth, 1),
  );
  const lastDateOfNextActiveMonth = endOfMonth(firstDayOfNextActiveMonth);

  const datesOfNextMonth = eachDayOfInterval({
    start: firstDayOfNextActiveMonth,
    end: lastDateOfNextActiveMonth,
  });

  datesInActiveMonth = [
    ...datesOfPreviousMonth.slice(datesOfPreviousMonth.length - firstDayIndex),
    ...datesInActiveMonth,
  ];

  // Calculate the minimum number of rows needed (5 or 6 rows)
  const totalDaysNeeded = Math.ceil(datesInActiveMonth.length / 7) * 7;

  datesInActiveMonth = [
    ...datesInActiveMonth,
    ...datesOfNextMonth.slice(0, totalDaysNeeded - datesInActiveMonth.length),
  ];

  return { startOfActiveMonth, datesInActiveMonth };
};

export function DatePicker({
  date1,
  setDate1,
  date2,
  setDate2,
  onChange = () => {},
}: {
  date1?: Date;
  setDate1: (date: Date) => void;
  date2?: Date;
  setDate2?: (date: Date) => void;
  onChange?: () => void;
}) {
  const today = new Date();
  const isRangeMode = !!setDate2;

  const [internalDate1, setInternalDate1] = useState<Date>(date1);
  const [internalDate2, setInternalDate2] = useState<Date>(date2);

  // Clear the time part of the date.
  today.setHours(0, 0, 0, 0);

  const [activeYear, setActiveYear] = useState(today.getFullYear());
  const [activeMonth, setActiveMonth] = useState(today.getMonth() + 1);

  // Compute dates for the left (active) month
  const { startOfActiveMonth, datesInActiveMonth } = computeDatesForMonth(
    activeYear,
    activeMonth,
  );

  // Compute dates for the right (next) month - only used in range mode
  const nextMonthDate = addMonths(startOfActiveMonth, 1);
  const {
    startOfActiveMonth: startOfNextMonth,
    datesInActiveMonth: datesInNextMonth,
  } = computeDatesForMonth(
    nextMonthDate.getFullYear(),
    nextMonthDate.getMonth() + 1,
  );

  const [activeDate, setActiveDate] = useState<
    "internalDate1" | "internalDate2"
  >("internalDate1");

  const [hoveringDate, setHoveringDate] = useState<Date>(null);

  // Layout rows:
  // Row 0: Chips (Set today, This month, Quick select)
  // Rows 1 to calendarRows: Calendar days
  // Row calendarRows+1: Date inputs
  // Row calendarRows+2: Buttons (Cancel, Confirm)
  const CHIPS_ROW = 0;
  const CALENDAR_ROW_START = 1;
  const leftMonthRows = Math.ceil(datesInActiveMonth.length / 7);
  const rightMonthRows = isRangeMode
    ? Math.ceil(datesInNextMonth.length / 7)
    : 0;
  const calendarRows = Math.max(leftMonthRows, rightMonthRows);
  const INPUTS_ROW = CALENDAR_ROW_START + calendarRows;
  const BUTTONS_ROW = INPUTS_ROW + 1;
  const totalRows = BUTTONS_ROW + 1;
  const totalCols = isRangeMode ? 14 : 7;

  const {
    id: contextId,
    ref: contextRef,
    node: contextNode,
  } = useContextScope();
  const { getCellProps } = useContextCellNavigation(
    contextId,
    contextRef,
    contextNode,
    {
      rowCount: totalRows,
      colCount: totalCols,
      wrap: false,
    },
  );

  // Helper to get date from cell position (only for calendar rows)
  const getDateFromCell = useCallback(
    (rowIndex: number, colIndex: number): Date | null => {
      const calendarRowIndex = rowIndex - CALENDAR_ROW_START;
      if (calendarRowIndex < 0 || calendarRowIndex >= calendarRows) return null;

      if (isRangeMode && colIndex >= 7) {
        // Right calendar
        const index = calendarRowIndex * 7 + (colIndex - 7);
        return datesInNextMonth[index] ?? null;
      } else {
        // Left calendar
        const index = calendarRowIndex * 7 + colIndex;
        return datesInActiveMonth[index] ?? null;
      }
    },
    [isRangeMode, datesInActiveMonth, datesInNextMonth, calendarRows],
  );

  // Handle Enter key to select the focused date or trigger click on chips/buttons
  const handleEnterKey = useCallback(
    (e: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement;
      if (!activeElement) return;

      const rowIndex = parseInt(activeElement.dataset.rowIndex ?? "", 10);
      const colIndex = parseInt(activeElement.dataset.colIndex ?? "", 10);
      if (isNaN(rowIndex) || isNaN(colIndex)) return;

      e.preventDefault();
      e.stopPropagation();

      // For chips, inputs, and buttons rows - trigger click on the element or its focusable child
      if (rowIndex === CHIPS_ROW || rowIndex === BUTTONS_ROW) {
        const clickTarget = activeElement.querySelector(
          "button, [role='button']",
        ) as HTMLElement;
        if (clickTarget) {
          clickTarget.click();
        } else {
          activeElement.click();
        }
        return;
      }

      // For inputs row - focus the input inside
      if (rowIndex === INPUTS_ROW) {
        const inputTarget = activeElement.querySelector("input") as HTMLElement;
        if (inputTarget) {
          inputTarget.focus();
        }
        return;
      }

      // For calendar cells
      if (
        rowIndex >= CALENDAR_ROW_START &&
        rowIndex < CALENDAR_ROW_START + calendarRows
      ) {
        const date = getDateFromCell(rowIndex, colIndex);
        if (!date) return;

        const setDateFn =
          activeDate === "internalDate2" ? setInternalDate2 : setInternalDate1;

        if (isRangeMode) {
          if (
            activeDate === "internalDate1" &&
            internalDate2 &&
            date > internalDate2
          ) {
            setInternalDate2(null);
          } else if (
            activeDate === "internalDate2" &&
            internalDate1 &&
            date < internalDate1
          ) {
            setInternalDate1(null);
          }
          setActiveDate(
            activeDate === "internalDate1" ? "internalDate2" : "internalDate1",
          );
        }
        setDateFn(date);
      }
    },
    [
      activeDate,
      isRangeMode,
      internalDate1,
      internalDate2,
      getDateFromCell,
      calendarRows,
    ],
  );

  useContextHotkeys({
    id: "datepicker-enter",
    combos: ["Enter", "Space"],
    callback: handleEnterKey,
    contextNode,
  });

  // Handle Cmd+Enter to confirm
  const handleConfirm = useCallback(() => {
    setDate1(internalDate1);
    if (setDate2) {
      setDate2(internalDate2);
    }
    onChange?.();
  }, [internalDate1, internalDate2, setDate1, setDate2, onChange]);

  useContextHotkeys({
    id: "datepicker-cmd-enter",
    combos: { mac: "cmd+enter", windows: "ctrl+enter", linux: "ctrl+enter" },
    callback: (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleConfirm();
    },
    contextNode,
  });

  let isDate1Active = true;
  let isDate2Active = true;
  let fromDate = null;
  let toDate = null;

  if (setDate2) {
    if (activeDate === "internalDate1") {
      if (hoveringDate) {
        fromDate = hoveringDate;
      } else {
        fromDate = internalDate1;
      }
      toDate = internalDate2;
    } else {
      fromDate = internalDate1;
      if (hoveringDate) {
        toDate = hoveringDate;
      } else {
        toDate = internalDate2;
      }
    }

    isDate1Active =
      (((fromDate && fromDate < toDate) || fromDate === hoveringDate) &&
        fromDate) ||
      (fromDate && !toDate);

    isDate2Active =
      (((toDate && toDate > fromDate) || toDate === hoveringDate) && toDate) ||
      (toDate && !fromDate);
  } else {
    fromDate = internalDate1;
    isDate1Active = fromDate != null;
  }
  const [isQuickSelectOpen, setIsQuickSelectOpen] = useState(false);

  return (
    <div
      ref={contextRef}
      className="min-w-0 p-2 flex flex-col flex-nowrap max-w-min gap-1"
    >
      {/* Date w/ chevrons */}
      <div
        className={cn(
          "min-w-0",
          "flex justify-between items-center mb-2",
          isRangeMode ? "gap-6" : "gap-1",
        )}
      >
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => {
            setActiveYear(getPreviousMonth(startOfActiveMonth).getFullYear());
            setActiveMonth(getPreviousMonth(startOfActiveMonth).getMonth() + 1);
          }}
        >
          <icons.chevronLeft />
        </Button>
        <Typography level="title-md" className="flex-1 text-center">
          {format(startOfActiveMonth, "MMMM yyyy")}
        </Typography>
        {isRangeMode && (
          <Typography level="title-md" className="flex-1 text-center">
            {format(startOfNextMonth, "MMMM yyyy")}
          </Typography>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => {
            setActiveYear(getNextMonth(startOfActiveMonth).getFullYear());
            setActiveMonth(getNextMonth(startOfActiveMonth).getMonth() + 1);
          }}
        >
          <icons.chevronLeft style={{ transform: "rotate(180deg)" }} />
        </Button>
      </div>

      <div className="min-w-0 flex justify-start flex-wrap gap-1 w-full">
        {/* Set today */}
        <div
          {...getCellProps({ rowIndex: CHIPS_ROW, colIndex: 0 })}
          tabIndex={0}
          className="rounded-sm [outline:none] focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-2"
        >
          <Badge variant="outline" asChild className="text-sm">
            <button
              type="button"
              onClick={() => {
                if (activeDate === "internalDate1") {
                  setInternalDate1(today);
                } else {
                  setInternalDate2(today);
                }
              }}
            >
              Set today
            </button>
          </Badge>
        </div>

        {/* Go to this month */}
        <div
          {...getCellProps({ rowIndex: CHIPS_ROW, colIndex: 1 })}
          tabIndex={0}
          className="rounded-sm [outline:none] focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-2"
        >
          <Badge variant="outline" asChild className="text-sm">
            <button
              type="button"
              onClick={() => {
                setActiveYear(today.getFullYear());
                setActiveMonth(today.getMonth() + 1);
              }}
            >
              This month
            </button>
          </Badge>
        </div>

        {/* Quick select */}
        {setDate2 && (
          <div
            {...getCellProps({ rowIndex: CHIPS_ROW, colIndex: 2 })}
            tabIndex={0}
            className="rounded-sm [outline:none] focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-2"
          >
            <Popper
              placement="bottom-end"
              isOpenState={[isQuickSelectOpen, setIsQuickSelectOpen]}
              anchor={
                <Badge variant="outline" asChild className="text-sm">
                  <button type="button" onClick={() => null}>
                    Quick select
                  </button>
                </Badge>
              }
            >
              <List<{
                key: string;
                label?: string;
                name?: string;
                isDivider?: boolean;
                onClick?: () => void;
              }>
                data={[
                  {
                    key: "quick-select",
                    name: "Quick select",
                    isDivider: true,
                  },
                  {
                    key: "this-month",
                    label: "This month",
                    onClick: () => {
                      setInternalDate1(startOfMonth(today));
                      setInternalDate2(endOfMonth(today));
                    },
                  },
                  {
                    key: "last-month",
                    label: "Last month",
                    onClick: () => {
                      setInternalDate1(startOfMonth(subMonths(today, 1)));
                      setInternalDate2(endOfMonth(subMonths(today, 1)));
                    },
                  },
                  {
                    key: "this-year",
                    label: "This year",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 0)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear(), 11)),
                      );
                    },
                  },
                  {
                    key: "last-year",
                    label: "Last year",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear() - 1, 0)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear() - 1, 11)),
                      );
                    },
                  },
                  {
                    key: "all-time",
                    label: "All time",
                    onClick: () => {
                      setInternalDate1(null);
                      setInternalDate2(null);
                    },
                  },
                  {
                    key: "quarters",
                    name: "Quarters",
                    isDivider: true,
                  },
                  {
                    key: "quarter-1",
                    label: "Q1",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 0)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear(), 2)),
                      );
                    },
                  },
                  {
                    key: "quarter-2",
                    label: "Q2",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 3)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear(), 5)),
                      );
                    },
                  },
                  {
                    key: "quarter-3",
                    label: "Q3",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 6)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear(), 8)),
                      );
                    },
                  },
                  {
                    key: "quarter-4",
                    label: "Q4",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 9)),
                      );
                      setInternalDate2(
                        endOfMonth(new Date(today.getFullYear(), 11)),
                      );
                    },
                  },
                  {
                    key: "to-date",
                    name: "To date",
                    isDivider: true,
                  },
                  {
                    key: "year-to-date",
                    label: "Year to date",
                    onClick: () => {
                      setInternalDate1(
                        startOfMonth(new Date(today.getFullYear(), 0)),
                      );
                      setInternalDate2(today);
                    },
                  },
                  {
                    key: "quarter-to-date",
                    label: "Quarter to date",
                    onClick: () => {
                      const quarter = Math.floor(today.getMonth() / 3);
                      setInternalDate1(
                        startOfMonth(
                          new Date(today.getFullYear(), quarter * 3),
                        ),
                      );
                      setInternalDate2(today);
                    },
                  },
                  {
                    key: "ranges",
                    name: "Ranges",
                    isDivider: true,
                  },
                  {
                    key: "last-30-days",
                    label: "Last 30 days",
                    onClick: () => {
                      setInternalDate1(subDays(today, 30));
                      setInternalDate2(today);
                    },
                  },
                  {
                    key: "last-90-days",
                    label: "Last 90 days",
                    onClick: () => {
                      setInternalDate1(subDays(today, 90));
                      setInternalDate2(today);
                    },
                  },
                  {
                    key: "last-6-months",
                    label: "Last 6 months",
                    onClick: () => {
                      setInternalDate1(subMonths(today, 6));
                      setInternalDate2(today);
                    },
                  },
                ].filter((item) => !item.isDivider)}
                onItemClick={(item) => {
                  setIsQuickSelectOpen(false);
                  item?.onClick();
                }}
                className="p-1"
              >
                <List.Items<{
                  key: string;
                  label?: string;
                  name?: string;
                  isDivider?: boolean;
                  onClick?: () => void;
                }>>
                  {(item) => (
                    <List.Item
                      value={item}
                      data-testid={`date-picker-quick-select-${item.key}`}
                    >
                      <Typography level="body-sm">{item.label}</Typography>
                    </List.Item>
                  )}
                </List.Items>
              </List>
            </Popper>
          </div>
        )}
      </div>

      {/* Calendar */}
      <div className="flex min-w-0 flex-row flex-nowrap gap-6 rounded-lg">
        {/* Left calendar (current month) */}
        <div className="min-w-0 flex-col">
          <div className="overflow-hidden min-w-fit">
            <div className="min-w-0 flex-wrap grid grid-cols-[repeat(7,32px)] auto-rows-[32px] gap-1">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="min-w-0 flex items-center justify-center cursor-pointer"
                >
                  <Typography level="body-md">{day}</Typography>
                </div>
              ))}
            </div>

            <CalendarMonth
              datesInActiveMonth={datesInActiveMonth}
              activeMonth={activeMonth}
              activeDate={activeDate}
              hoveringDate={hoveringDate}
              setHoveringDate={setHoveringDate}
              setActiveDate={setActiveDate}
              internalDate1={internalDate1}
              setInternalDate1={setInternalDate1}
              internalDate2={internalDate2}
              setInternalDate2={setInternalDate2}
              includeDate2={!!setDate2}
              getCellProps={getCellProps}
              rowOffset={CALENDAR_ROW_START}
              colOffset={0}
            />
          </div>
        </div>

        {/* Right calendar (next month) - only shown in range mode */}
        {isRangeMode && (
          <div className="min-w-0 flex-col">
            <div className="overflow-hidden min-w-fit">
              <div className="min-w-0 flex-wrap grid grid-cols-[repeat(7,32px)] auto-rows-[32px] gap-1">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div
                      key={`next-${day}`}
                      className="min-w-0 flex items-center justify-center cursor-pointer"
                    >
                      <Typography level="body-md">{day}</Typography>
                    </div>
                  ),
                )}
              </div>

              <CalendarMonth
                datesInActiveMonth={datesInNextMonth}
                activeMonth={startOfNextMonth.getMonth() + 1}
                activeDate={activeDate}
                hoveringDate={hoveringDate}
                setHoveringDate={setHoveringDate}
                setActiveDate={setActiveDate}
                internalDate1={internalDate1}
                setInternalDate1={setInternalDate1}
                internalDate2={internalDate2}
                setInternalDate2={setInternalDate2}
                includeDate2={true}
                getCellProps={getCellProps}
                rowOffset={CALENDAR_ROW_START}
                colOffset={7}
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <div
        className={cn(
          "min-w-0",
          "mt-2 flex",
          isRangeMode ? "flex-row" : "flex-col",
          "items-stretch",
          isRangeMode ? "gap-4" : "gap-1",
          "w-full",
        )}
      >
        <div
          {...getCellProps({ rowIndex: INPUTS_ROW, colIndex: 0 })}
          className="flex flex-row justify-between items-center flex-1 min-w-0 gap-2 rounded-sm focus-within:[outline:none]"
        >
          {setDate1 && (
            <DateInput
              date={fromDate}
              otherDate={toDate}
              setDate={setInternalDate1}
              setOtherDate={setInternalDate2}
              isDateActive={isDate1Active}
              activeDate={"internalDate1"}
              setActiveDate={setActiveDate}
              setActiveYear={setActiveYear}
              setActiveMonth={setActiveMonth}
              emptyText={setDate2 ? `Enter start date` : `Enter date`}
            />
          )}
        </div>
        {setDate2 && (
          <div
            {...getCellProps({ rowIndex: INPUTS_ROW, colIndex: 7 })}
            className="flex flex-row justify-between items-center flex-1 min-w-0 gap-2 rounded-sm focus-within:[outline:none]"
          >
            <DateInput
              date={toDate}
              otherDate={fromDate}
              setDate={setInternalDate2}
              setOtherDate={setInternalDate1}
              isDateActive={isDate2Active}
              activeDate={"internalDate2"}
              setActiveDate={setActiveDate}
              setActiveYear={setActiveYear}
              setActiveMonth={setActiveMonth}
              emptyText={`Enter end date`}
            />
          </div>
        )}
      </div>

      {/* actions */}
      <div className="min-w-0 flex justify-end gap-2 mt-2">
        <div
          {...getCellProps({ rowIndex: BUTTONS_ROW, colIndex: 0 })}
          tabIndex={0}
          className="rounded-sm focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-2"
        >
          <Button
            variant="outline"
            tabIndex={-1}
            onClick={() => {
              setInternalDate1(null);
              setInternalDate2(null);
              onChange?.();
            }}
          >
            Cancel
          </Button>
        </div>
        <div
          {...getCellProps({ rowIndex: BUTTONS_ROW, colIndex: 1 })}
          tabIndex={0}
          className="rounded-sm focus-visible:[outline:2px_solid] focus-visible:outline-ring focus-visible:outline-offset-2"
        >
          <Button
            tabIndex={-1}
            // disabled if the dates are the same or if the dates started null and are still null
            disabled={
              (datesEqual(internalDate1, date1) &&
                datesEqual(internalDate2, date2)) ||
              (!date1 && !internalDate1 && !internalDate2)
            }
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
